import { Injectable } from '@angular/core';
import { CellRange, normalizeRange } from '../core/models/cell.models';
import { ChildTableA11yService } from './child-table-a11y.service';

/**
 * Manages cell-range selection state and drag-to-select behaviour
 * for the child table grid.
 *
 * Component-scoped (provided via the host component's `providers` array).
 */
@Injectable()
export class ChildTableSelectionService {
  cellRange: CellRange | null = null;
  isDraggingSelection = false;

  constructor(private readonly a11yService: ChildTableA11yService) {}

  /** Is the cell inside the current selection rectangle? */
  isCellInRange(rowIndex: number, colIndex: number): boolean {
    if (!this.cellRange) return false;
    const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.cellRange);
    return rowIndex >= minRow && rowIndex <= maxRow && colIndex >= minCol && colIndex <= maxCol;
  }

  /** Is the cell on a specific edge of the selection rectangle? Used for border rendering. */
  isRangeEdge(rowIndex: number, colIndex: number, edge: 'top' | 'bottom' | 'left' | 'right'): boolean {
    if (!this.cellRange) return false;
    const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.cellRange);
    if (rowIndex < minRow || rowIndex > maxRow || colIndex < minCol || colIndex > maxCol) return false;
    switch (edge) {
      case 'top': return rowIndex === minRow;
      case 'bottom': return rowIndex === maxRow;
      case 'left': return colIndex === minCol;
      case 'right': return colIndex === maxCol;
    }
  }

  /** Clear the selection range (e.g. on Escape or page change). */
  clearCellRange(): void {
    this.cellRange = null;
  }

  /** Announce range dimensions when it spans more than one cell (WCAG 4.1.3). */
  announceRangeIfMultiCell(): void {
    if (!this.cellRange) return;
    const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.cellRange);
    const rows = maxRow - minRow + 1;
    const cols = maxCol - minCol + 1;
    if (rows > 1 || cols > 1) {
      this.a11yService.announce(`${rows} row${rows > 1 ? 's' : ''}, ${cols} column${cols > 1 ? 's' : ''} selected`);
    }
  }

  /**
   * Begin or extend a drag selection on mousedown.
   * Pure selection state only — guards and edit-save logic stay in the component.
   */
  onCellMouseDown(shiftKey: boolean, rowIndex: number, colIndex: number): void {
    if (shiftKey && this.cellRange) {
      this.cellRange = { ...this.cellRange, focusRow: rowIndex, focusCol: colIndex };
    } else {
      this.cellRange = { anchorRow: rowIndex, anchorCol: colIndex, focusRow: rowIndex, focusCol: colIndex };
    }
    this.isDraggingSelection = true;
  }

  /**
   * Extend range as mouse moves over cells while dragging.
   * @returns `true` if the range was extended, `false` if not dragging.
   */
  onCellMouseEnter(rowIndex: number, colIndex: number): boolean {
    if (!this.isDraggingSelection || !this.cellRange) return false;
    this.cellRange = { ...this.cellRange, focusRow: rowIndex, focusCol: colIndex };
    return true;
  }

  /**
   * End drag selection on mouseup.
   * @returns `true` if a drag was in progress and was ended.
   */
  onDocumentMouseUp(): boolean {
    if (this.isDraggingSelection) {
      this.isDraggingSelection = false;
      return true;
    }
    return false;
  }
}
